import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { LucideAngularModule, Search } from 'lucide-angular';
import { SearchService } from '@app/core/services/search.service';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounce } from '@app/core/utils/debounce';
import { OverlayMenuComponent } from '@shared/components/overlay-menu/overlay-menu.component';
import { TranslatePipe } from '@ngx-translate/core';
import { SearchConfig } from '@shared/components/input/search/search-config.interface';
import { SearchDefaultConfig } from '@shared/components/input/search/search-default-config';
import { NgClass } from '@angular/common';
import { SearchResult, SearchResultCollection } from '@shared/components/input/search/search-result.interface';

@Component({
  selector: 'app-input-search',
  imports: [
    LucideAngularModule,
    ReactiveFormsModule,
    OverlayMenuComponent,
    TranslatePipe,
    NgClass,
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.css'
})
export class SearchComponent implements OnInit, AfterViewInit {
  @ViewChild('searchInput', { static: false }) public searchInput?: ElementRef;
  @ViewChild('searchOverlayMenu') public searchOverlayMenu!: OverlayMenuComponent;

  /** Config **/
  @Input() public config?: SearchConfig;
  public searchConfig: SearchConfig = SearchDefaultConfig;

  /** Emit Search **/
  @Output() public result: EventEmitter<any> = new EventEmitter();

  /** Emit Hotkey **/
  @Output() public hotkey: EventEmitter<string> = new EventEmitter();

  /** Search **/
  @Output() public searchQuery: EventEmitter<string> = new EventEmitter();

  /** Global Hotkey **/
  @HostListener('document:keydown.meta.k', ['$event'])
  public focusSearch(e: Event) {
    if (e instanceof KeyboardEvent) {
      if (this.searchInput && this.searchConfig.hotkeys) {
        this.searchInput.nativeElement.focus();
      }
    }
  }

  @HostListener('document:keydown.meta.g', ['$event'])
  public toggleAdvancedSearch(e: Event) {
    if (e instanceof KeyboardEvent) {
      this.hotkey.emit(e.key);
    }
  }

  /** Local Hotkey **/
  @HostListener('document:keydown', ['$event'])
  public performSearch(event: Event) {
    if (this.isComponentInFocus && event instanceof KeyboardEvent) {
      switch (event.key) {
        case 'Enter':
          /** If no element is focused, emit the search string **/
          if (this.elementFocus === -1) {
            this.emitSearch(this.searchForm.value);
            return;
          }

          if (this.searchInput && this.config?.referToSearch) {
            this.searchService.addQuery(this.searchInput?.nativeElement.value);

            if (this.searchConfig.navigateUrl) {
              this.navigateToSearch(
                this.searchConfig.navigateUrl,
                this.searchInput.nativeElement.value
              );
            }
          }

          break;
        case 'ArrowDown':
          event.preventDefault();
          break;
        case 'ArrowUp':
          event.preventDefault();
          break;
        case 'Escape':
          this.value = '';
          this.autoCompleteSuggestions = [];
          break;
      }
    }
  }

  /** Focus Controllers **/
  @HostListener('focusin', ['$event'])
  public onFocusOut(event: FocusEvent) {
    this.isComponentInFocus = true;
    if (this.value && this.autoCompleteSuggestions.length > 0) {
      this.searchOverlayMenu.open();
    }
  }

  @HostListener('focusout', ['$event'])
  public onBlur(event: FocusEvent) {
    this.isComponentInFocus = false;
  }

  public elementFocus: number = -1;

  /** Component Focused **/
  public isComponentInFocus: boolean = false;

  /** Icons **/
  readonly searchIcon = Search

  /** Debounce Handler **/
  private readonly debounceSearch: (value: string) => void = debounce(this.handleSearchChange.bind(this), 300);

  /** Search FormControl **/
  public searchForm: FormControl = new FormControl('');

  /** Autocomplete Suggestions **/
  public autoCompleteSuggestions: any[] = [];

  /** Search Result Collection **/
  public searchResultCollection: SearchResultCollection[] = [];

  /** Value **/
  public value: string = '';

  constructor(
    private readonly searchService: SearchService,
    private readonly router: Router,
  ) {

    this.searchForm.valueChanges.subscribe((value: string) => {
      const val = value.trim();
      this.debounceSearch(val);
    });
  }

  public ngOnInit(): void {
    /** Merge Config **/
    if (this.config) {
      this.searchConfig = {
        ...this.searchConfig,
        ...this.config,
      }
    }
  }

  public ngAfterViewInit(): void {
    if (this.searchConfig) {
      if (this.searchConfig.autoFocus) {
        setTimeout(() => {
          this.searchInput?.nativeElement.focus();
        }, 0);
      }
    }
  }

  public handleSearchChange(value: string): void {
    this.value = value;

    if (value.length == 0) {
      this.autoCompleteSuggestions = [];
    }

    /** Use Callback Service if defined **/
    if (this.searchConfig.callBackFunction && typeof this.searchConfig.callBackFunction === 'function') {
      this.searchConfig.callBackFunction(value).subscribe((result: SearchResultCollection[]) => {
        this.searchResultCollection = result;
      });
    }
  }

  public navigateToSearch(url: string, id?: number | string): void {
    this.router.navigate([url, String(id)]);
  }

  /** Search Result Clicked - Refer to data collection **/
  public searchResultClicked(item: SearchResult): void {
    if (item?.id) {
      /** If there's a navigateUrl defined in the config, take the user there **/
      if (this.searchConfig.navigateUrl) {
        this.navigateToSearch(this.searchConfig.navigateUrl, item.id);
      }

      /** Emit result **/
      this.result.emit(item);

      /** Clear the Input **/
      this.clearInput();
    }
  }

  public emitSearch(value: string): void {
    this.searchQuery.emit(value);

    this.value = '';
    this.searchForm.patchValue(this.value);
  }

  public clearInput(): void {
    /** Clear Input **/
    this.value = '';
    this.searchForm.patchValue(this.value);
    /** Defocus Element **/
    this.searchInput?.nativeElement.blur();
  }
}
